import { create } from "zustand";
import { LINK_RELATIONS, LINK_RELATION_DEFAULT, type LinkRelation } from "@/lib/taxonomy";
import { broadcastSync } from "@/lib/sync-broadcast";
import {
  hydrateStateDoc,
  initialSyncState,
  persistStateDoc,
  pushStateDoc,
  syncStateDoc,
  type StateDocSyncSlice,
} from "@/lib/bff-state-doc";

/**
 * Global traceability links store (Track A · P0.2 skeleton → CC-LINKS
 * stage 2). Unlike the per-workshop record stores this is a **flat global
 * list**: a link can join two records in *different* workshops (the
 * cross-workshop "why did we decide this?" graph). It therefore can't use
 * `createWorkshopRecordStore` (which buckets by workshop); it's a small
 * hand-written store.
 *
 * Persistence — the same dual backend as the record stores (see
 * `@/lib/bff-state-doc`): IndexedDB only by default (`xaes-links`), or a
 * shared JSON blob at `workshops/_program/links.json` when
 * `VITE_WORKSHOP_BACKEND=bff`. The traceability graph IS the answer to "why
 * did we decide this?", so it must be shared across the team rather than
 * stranded in one browser. On the BFF backend the store hydrates from the
 * shared copy, writes through optimistically, and reconciles on the 15s /
 * focus / cross-tab poll (registered in `SYNCED_STORES`). Reconcile is an
 * id-union (shared copy wins on conflict, local-only links are kept) — an
 * append/remove log doesn't need recency; the one documented edge is that a
 * link removed on another device lingers in this browser's cache until that
 * device's removal is the authoritative copy, matching the record stores.
 *
 * **No stored reverse edges.** A link is stored once (`from → to`);
 * reverse lookups ("what points at this record?") are computed on read
 * from the flat list (see `src/lib/links.ts`).
 */

/** The record kinds a link endpoint can reference. Supersets the R1
 *  `SearchCategory` with the Track-A registers (finding/opportunity/
 *  recommendation/note). */
export type LinkEndpointKind =
  | "decision"
  | "action"
  | "assumption"
  | "capture"
  | "upload"
  | "finding"
  | "opportunity"
  | "recommendation"
  | "note";

export const LINK_ENDPOINT_KINDS: LinkEndpointKind[] = [
  "decision",
  "action",
  "assumption",
  "capture",
  "upload",
  "finding",
  "opportunity",
  "recommendation",
  "note",
];

/** One end of a link: a record identified by (kind, workshop, id). */
export type LinkEndpoint = {
  kind: LinkEndpointKind;
  workshopId: string;
  id: string;
};

export type Link = {
  id: string;
  from: LinkEndpoint;
  to: LinkEndpoint;
  relation: LinkRelation;
  createdAt: number;
  addedBy: string;
  /** Reserved seam for the legacy GitHub JSON-record mirror; unused (the
   *  shared copy now rides the BFF `workshops` tier, see jsdoc). Carried so
   *  old blobs round-trip cleanly. */
  githubPath?: string;
  githubSha?: string;
  githubSyncedAt?: string;
};

const IDB_KEY = "xaes-links";
const BFF_PATH = "_program/links.json";

type LinksStore = StateDocSyncSlice & {
  links: Link[];
  isHydrated: boolean;
  /** Set when a BFF hydrate couldn't reach the service (cached copy shown). */
  loadError: string | null;
  hydrate: () => Promise<void>;
  add: (link: Link) => void;
  remove: (id: string) => void;
  /** One-time cutover push of the local link graph to the shared backend
   *  (IDB→BFF migration). Returns the number of links pushed, 0 when the BFF
   *  backend is disabled. */
  pushLocalToBff: () => Promise<number>;
};

const KINDS = new Set<string>(LINK_ENDPOINT_KINDS);

/** Validate one raw endpoint; returns null when unusable. */
function sanitiseEndpoint(raw: unknown): LinkEndpoint | null {
  if (typeof raw !== "object" || raw === null) return null;
  const e = raw as Record<string, unknown>;
  if (typeof e.id !== "string") return null;
  if (typeof e.workshopId !== "string") return null;
  // Tolerate an unknown kind rather than dropping the whole link — a future
  // store kind shouldn't erase existing links written by a newer build.
  const kind = (KINDS.has(e.kind as string) ? e.kind : "decision") as LinkEndpointKind;
  return { kind, workshopId: e.workshopId, id: e.id };
}

function sanitise(raw: unknown): Link[] {
  if (!Array.isArray(raw)) return [];
  const out: Link[] = [];
  for (const item of raw) {
    if (typeof item !== "object" || item === null) continue;
    const l = item as Record<string, unknown>;
    if (typeof l.id !== "string") continue;
    const from = sanitiseEndpoint(l.from);
    const to = sanitiseEndpoint(l.to);
    if (!from || !to) continue;
    const relation: LinkRelation = LINK_RELATIONS.includes(
      l.relation as LinkRelation,
    )
      ? (l.relation as LinkRelation)
      : LINK_RELATION_DEFAULT;
    out.push({
      id: l.id,
      from,
      to,
      relation,
      createdAt: typeof l.createdAt === "number" ? l.createdAt : Date.now(),
      addedBy: typeof l.addedBy === "string" ? l.addedBy : "",
      githubPath: typeof l.githubPath === "string" ? l.githubPath : undefined,
      githubSha: typeof l.githubSha === "string" ? l.githubSha : undefined,
      githubSyncedAt:
        typeof l.githubSyncedAt === "string" ? l.githubSyncedAt : undefined,
    });
  }
  return out;
}

/** Id-union reconcile: shared copy wins on conflict, local-only links kept. */
function mergeLinks(remote: Link[] | null, local: Link[]): Link[] {
  if (!remote) return local;
  const seen = new Set(remote.map((l) => l.id));
  return [...remote, ...local.filter((l) => !seen.has(l.id))];
}

export const useLinks = create<LinksStore>((set, get) => ({
  links: [],
  isHydrated: false,
  loadError: null,
  syncState: initialSyncState(),
  lastSyncedAt: null,
  syncError: null,
  hydrate: async () => {
    if (get().isHydrated) return;
    const { value, loadError } = await hydrateStateDoc({
      idbKey: IDB_KEY,
      path: BFF_PATH,
      parse: sanitise,
      merge: mergeLinks,
    });
    set({ links: value, isHydrated: true, loadError });
  },
  sync: async () => {
    if (get().syncState === "disabled") return;
    set({ syncState: "syncing", syncError: null });
    const { value, state, error } = await syncStateDoc({
      idbKey: IDB_KEY,
      path: BFF_PATH,
      parse: sanitise,
      merge: mergeLinks,
      local: get().links,
    });
    set({
      links: value,
      isHydrated: true,
      syncState: state,
      syncError: error,
      lastSyncedAt: state === "synced" ? Date.now() : get().lastSyncedAt,
    });
  },
  add: (link) => {
    const next = [...get().links, link];
    set({ links: next });
    persistStateDoc({
      idbKey: IDB_KEY,
      path: BFF_PATH,
      value: next,
      parse: sanitise,
      merge: mergeLinks,
      onReconciled: (links) => set({ links }),
    });
    broadcastSync();
  },
  remove: (id) => {
    const next = get().links.filter((l) => l.id !== id);
    set({ links: next });
    persistStateDoc({
      idbKey: IDB_KEY,
      path: BFF_PATH,
      value: next,
      parse: sanitise,
      merge: mergeLinks,
      onReconciled: (links) => set({ links }),
    });
    broadcastSync();
  },
  pushLocalToBff: async () => {
    const links = get().links;
    const pushed = await pushStateDoc(BFF_PATH, links, sanitise, mergeLinks);
    return pushed ? links.length : 0;
  },
}));
